<template>
  <article class="procedure-card">
    <header class="procedure-card__header">
      <div class="procedure-card__title-block">
        <div class="procedure-card__icon">
          <i :class="iconClass" aria-hidden="true"></i>
        </div>
        <div>
          <h3 class="procedure-card__title">{{ request.requestTitle || definition?.name || "---" }}</h3>
          <p class="procedure-card__type">{{ definition?.name || "---" }}</p>
        </div>
      </div>
      <Tag :severity="statusSeverity" :value="statusLabel" />
    </header>

    <button
      v-if="mode === 'approver'"
      type="button"
      class="procedure-card__employee"
      @click="$emit('employee', request.employeeId)"
    >
      <img
        v-if="employeeAvatarUrl"
        :src="employeeAvatarUrl"
        :alt="employeeName"
        class="procedure-card__avatar"
        @error="handleAvatarError"
      />
      <span v-else class="procedure-card__avatar-fallback">{{ employeeInitials }}</span>
      <div class="procedure-card__employee-copy">
        <span class="procedure-card__employee-name">{{ employeeName }}</span>
        <span v-if="request.requestDate" class="procedure-card__employee-date">
          {{ formatLongDateTime(request.requestDate, true) }}
        </span>
      </div>
    </button>

    <dl class="procedure-card__details">
      <div v-if="request.requestDate" class="procedure-card__detail">
        <dt>{{ t("wkt.client.procedures.procedure-card.requested_date") }}</dt>
        <dd>{{ formatLongDateTime(request.requestDate, true) }}</dd>
      </div>
      <div
        v-if="request.requestInfo"
        class="procedure-card__detail procedure-card__detail--full procedure-card__detail--priority"
      >
        <dt>{{ definition?.freeTextLabel || t("wkt.client.procedures.table.headers.request") }}</dt>
        <dd>{{ request.requestInfo }}</dd>
      </div>
      <div v-if="request.fromDate" class="procedure-card__detail">
        <dt>{{ definition?.fromDateLabel || t("wkt.client.procedures.procedure-card.start_date") }}</dt>
        <dd>{{ formatSimpleDate(request.fromDate) }}</dd>
      </div>
      <div v-if="request.toDate" class="procedure-card__detail">
        <dt>{{ definition?.toDateLabel || t("wkt.client.procedures.procedure-card.end_date") }}</dt>
        <dd>{{ formatSimpleDate(request.toDate) }}</dd>
      </div>
      <div v-if="projectLabel" class="procedure-card__detail">
        <dt>{{ definition?.projectLabel || t("wkt.client.procedures.cbo_projects") }}</dt>
        <dd>{{ projectLabel }}</dd>
      </div>
      <div v-if="request.quantity != null" class="procedure-card__detail">
        <dt>{{ definition?.quantityLabel || t("wkt.client.procedures.edit-window.quantity_placeholder") }}</dt>
        <dd>{{ request.quantity }}</dd>
      </div>
      <div
        v-if="request.approverComment && mode === 'history'"
        class="procedure-card__detail procedure-card__detail--full"
      >
        <dt>{{ t("wkt.client.procedures.procedure-card.comments") }}</dt>
        <dd>{{ request.approverComment }}</dd>
      </div>
      <div
        v-for="detail in balanceDetails"
        :key="detail.label"
        class="procedure-card__detail"
      >
        <dt>{{ detail.label }}</dt>
        <dd :class="detail.tone ? `procedure-card__value--${detail.tone}` : null">{{ detail.value }}</dd>
      </div>
    </dl>

    <div v-if="request.adminId" class="procedure-card__meta-note">
      {{ t("wkt.client.procedures.created_by_admin") }}
    </div>

    <div v-if="request.documents?.length" class="procedure-card__documents">
      <span class="procedure-card__documents-title">{{ t("wkt.client.procedures.table.headers.attached") }}</span>
      <div class="procedure-card__document-list">
        <button
          v-for="document in request.documents"
          :key="document.id ?? document.documentName ?? 'document'"
          type="button"
          class="procedure-card__document-link"
          @click="downloadDocument(document.id, document.documentName)"
        >
          {{ document.documentName || "---" }}
        </button>
      </div>
    </div>

    <div v-if="secondaryLinkLabel" class="procedure-card__links">
      <button
        type="button"
        class="procedure-card__link"
        @click="handleSecondaryLink"
      >
        {{ secondaryLinkLabel }}
      </button>
    </div>

    <footer class="procedure-card__actions">
      <Button
        v-if="mode === 'approver'"
        class="footer-primary-button"
        text
        :label="t('wkt.client.common.dialog-buttons.approve')"
        @click="$emit('approve', request.id)"
      />
      <Button
        v-if="mode === 'approver'"
        class="footer-button-cancel"
        text
        :label="t('wkt.client.common.dialog-buttons.decline')"
        @click="$emit('deny', request.id)"
      />
      <Button
        v-if="mode === 'own-pending'"
        class="footer-primary-button"
        text
        :label="t('wkt.client.common.dialog-buttons.edit')"
        @click="$emit('edit', request.id)"
      />
      <Button
        v-if="mode === 'history'"
        class="footer-primary-button"
        text
        :label="t('wkt.client.procedures.procedure-card.show')"
        @click="$emit('show', request.id)"
      />
    </footer>
  </article>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useDateFormatter } from "@wkt/composables/useDateFormatter";
import { useUtils } from "@wkt/composables/useUtils";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import {
  ProcedureDefinitionClass,
  ProcedureRequestStatus,
  type FullProcedureDefinitionDTO,
  type FullProcedureRequestDTO,
} from "@wkt/api/procedures";

const props = defineProps<{
  request: FullProcedureRequestDTO;
  definition?: FullProcedureDefinitionDTO | null;
  mode: "own-pending" | "history" | "approver";
}>();

const emit = defineEmits<{
  edit: [requestId: number];
  show: [requestId: number];
  approve: [requestId: number];
  deny: [requestId: number];
  employee: [employeeId?: number | null];
  ownCalendar: [];
  approverCalendar: [requestId: number];
  expense: [expenseSheetId: number];
}>();

const { t } = useI18n();
const { formatSimpleDate, formatLongDateTime } = useDateFormatter();
const { getProfileAssetUrl } = useUtils();
const sessionInformation = computed(() => gwtApi.getSessionInformation() as any);
const avatarFailed = ref(false);

const employeeName = computed(() => {
  const firstname = props.request.firstname?.trim() || "";
  const lastname = props.request.lastname?.trim() || "";
  return `${firstname} ${lastname}`.trim() || "---";
});

const employeeInitials = computed(() => {
  const firstname = props.request.firstname?.trim().charAt(0) || "";
  const lastname = props.request.lastname?.trim().charAt(0) || "";
  return `${firstname}${lastname}`.toUpperCase() || "--";
});

const employeeAvatarUrl = computed(() => {
  const profilePicture = props.request.profilePicture?.trim();
  if (!profilePicture || avatarFailed.value) {
    return "";
  }

  return getProfileAssetUrl(profilePicture);
});

const iconClass = computed(() => {
  const iconType = props.definition?.iconType || "pi pi-file";
  return iconType.startsWith("pi ") ? iconType : `pi pi-file`;
});

const projectLabel = computed(() => {
  if (!props.request.projectId) {
    return "";
  }

  const shortName = props.request.projectShortName?.trim();
  const description = props.request.projectDescription?.trim();

  if (shortName && description) {
    return `${shortName} - ${description}`;
  }

  return shortName || description || "---";
});

const statusLabel = computed(() => {
  switch (props.request.requestStatus) {
    case ProcedureRequestStatus.PREPARING:
      return t("wkt.client.procedures.procedure-card.preparing_status");
    case ProcedureRequestStatus.PENDING:
      return t("wkt.client.procedures.procedure-card.pending_status");
    case ProcedureRequestStatus.APPROVED:
      return t("wkt.client.procedures.procedure-card.approved_status");
    case ProcedureRequestStatus.DECLINED:
      return t("wkt.client.procedures.procedure-card.declined_status");
    case ProcedureRequestStatus.CANCELED:
      return t("wkt.client.procedures.procedure-card.canceled_status");
    case ProcedureRequestStatus.PROCESSED:
      return t("wkt.client.procedures.procedure-card.completed_status");
    default:
      return "---";
  }
});

const statusSeverity = computed(() => {
  switch (props.request.requestStatus) {
    case ProcedureRequestStatus.PENDING:
      return "warn";
    case ProcedureRequestStatus.APPROVED:
      return "success";
    case ProcedureRequestStatus.DECLINED:
      return "danger";
    case ProcedureRequestStatus.PROCESSED:
      return "info";
    default:
      return "secondary";
  }
});

const balanceDetails = computed(() => {
  if (
    props.request.hoursBagType == null &&
    props.request.hoursBagDelta == null &&
    props.request.vacationDeltaOld == null &&
    props.request.extraDeltaOld == null &&
    !props.request.paidAbsenceCategoryName
  ) {
    return [];
  }

  const details: Array<{ label: string; value: string; tone?: "positive" | "negative" }> = [];
  const hasLegacyBalances =
    props.request.vacationDeltaOld != null &&
    props.request.extraDeltaOld != null &&
    props.request.vacationDeltaOld !== 0 &&
    props.request.extraDeltaOld !== 0;

  if (hasLegacyBalances) {
    details.push({
      label: t("wkt.client.procedures.procedure-card.vacations"),
      value: formatSignedDays(props.request.vacationDeltaOld),
      tone: getTone(props.request.vacationDeltaOld),
    });
    details.push({
      label: t("wkt.client.vacations.free_days"),
      value: formatSignedDays(props.request.extraDeltaOld),
      tone: getTone(props.request.extraDeltaOld),
    });
    return details;
  }

  if (props.request.hoursBagType != null || props.request.paidAbsenceCategoryName) {
    details.push({
      label: t("wkt.client.procedures.bag_hours"),
      value: hoursBagLabel.value,
    });
  }

  if (props.request.hoursBagDelta != null && props.request.hoursBagDelta !== 0) {
    details.push({
      label: t("wkt.client.vacations-diference-days"),
      value: formatSignedDays(props.request.hoursBagDelta),
      tone: getTone(props.request.hoursBagDelta),
    });
  }

  return details;
});

const hoursBagLabel = computed(() => {
  const paidAbsenceCategoryName = props.request.paidAbsenceCategoryName?.trim();
  if (paidAbsenceCategoryName) {
    return paidAbsenceCategoryName;
  }

  const hoursBagType = props.request.hoursBagType;
  if (hoursBagType != null) {
    return getHoursBagLabel(hoursBagType);
  }

  return "---";
});

const secondaryLinkLabel = computed(() => {
  if (props.request.requestStatus !== ProcedureRequestStatus.PENDING) {
    return "";
  }

  if (props.request.expenseSheetId) {
    return t("wkt.client.procedures.procedure-card.show_expense_sheet");
  }

  if (props.definition?.procedureClass === ProcedureDefinitionClass.VACATION_LEAVE) {
    return props.mode === "approver"
      ? t("wkt.client.procedures.procedure-card.show_calendar")
      : t("wkt.client.procedures.procedure-card.show_my_calendar");
  }

  return "";
});

watch(
  () => props.request.profilePicture,
  () => {
    avatarFailed.value = false;
  }
);

function handleAvatarError(): void {
  avatarFailed.value = true;
}

function handleSecondaryLink(): void {
  if (props.request.expenseSheetId) {
    emit("expense", props.request.expenseSheetId);
    return;
  }

  if (props.mode === "approver") {
    emit("approverCalendar", props.request.id);
    return;
  }

  emit("ownCalendar");
}

function downloadDocument(documentId?: number, documentName?: string | null): void {
  if (!documentId || !props.request.id) {
    return;
  }

  gwtApi.downloadFile(
    `user/getRequestDocument/${props.request.id}/${documentId}`,
    documentName || "document"
  );
}

function formatSignedDays(days?: number | null): string {
  const value = days ?? 0;
  const absValue = Math.abs(value);
  const suffix =
    absValue === 1
      ? t("wkt.client.procedures.day")
      : t("wkt.client.procedures.days");

  return `${value} ${suffix}`;
}

function getTone(days?: number | null): "positive" | "negative" | undefined {
  if (days == null || days === 0) {
    return undefined;
  }

  return days > 0 ? "positive" : "negative";
}

function getHoursBagLabel(hoursBagType: number): string {
  const company = sessionInformation.value?.company;
  if (!company) {
    return String(hoursBagType);
  }

  switch (hoursBagType) {
    case 6:
      return company.defaultYearlyVacationLabel || String(hoursBagType);
    case 7:
      return company.defaultYearlyExtraLabel || String(hoursBagType);
    case 1:
      return company.defaultYearlyHoursBag1Label || String(hoursBagType);
    case 2:
      return company.defaultYearlyHoursBag2Label || String(hoursBagType);
    case 3:
      return company.defaultYearlyHoursBag3Label || String(hoursBagType);
    case 4:
      return company.defaultYearlyHoursBag4Label || String(hoursBagType);
    case 5:
      return company.defaultYearlyHoursBag5Label || String(hoursBagType);
    default:
      return String(hoursBagType);
  }
}
</script>

<style scoped>
.procedure-card {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 0.95rem;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  background: #fff;
}

.procedure-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.procedure-card__title-block {
  display: flex;
  gap: 0.875rem;
  min-width: 0;
}

.procedure-card__icon {
  width: 2rem;
  height: 2rem;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--primary-rgb), 0.1);
  color: var(--primary-color);
  flex-shrink: 0;
}

.procedure-card__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #12263a;
}

.procedure-card__type {
  margin: 0.15rem 0 0;
  font-size: 0.85rem;
  color: #52667a;
}

.procedure-card__employee {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
}

.procedure-card__avatar-fallback {
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--primary-rgb), 0.1);
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 700;
  flex-shrink: 0;
}

.procedure-card__avatar {
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  object-fit: cover;
  flex-shrink: 0;
  background: #eef3f8;
}

.procedure-card__employee-copy {
  display: flex;
  flex-direction: column;
}

.procedure-card__employee-name {
  font-weight: 600;
  color: #12263a;
}

.procedure-card__employee-date {
  font-size: 0.82rem;
  color: #6b7c93;
}

.procedure-card__details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
  margin: 0;
}

.procedure-card__detail {
  min-width: 0;
}

.procedure-card__detail--full {
  grid-column: 1 / -1;
}

.procedure-card__detail dt {
  margin: 0 0 0.25rem;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7b8ba1;
}

.procedure-card__detail dd {
  margin: 0;
  color: #213547;
  word-break: break-word;
}

.procedure-card__value--positive {
  color: #2f855a;
}

.procedure-card__value--negative {
  color: #c53030;
}

.procedure-card__meta-note {
  color: #6b7c93;
  font-size: 0.85rem;
}

.procedure-card__documents {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.procedure-card__documents-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #52667a;
}

.procedure-card__document-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.procedure-card__document-link,
.procedure-card__link {
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  color: var(--primary-color);
  text-decoration: underline;
  cursor: pointer;
}

.procedure-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.1rem;
  border-top: 1px solid #edf1f5;
}

@media (max-width: 640px) {
  .procedure-card {
    gap: 0.65rem;
    padding: 0.8rem;
  }

  .procedure-card__header {
    gap: 0.75rem;
  }

  .procedure-card__title {
    font-size: 0.95rem;
    line-height: 1.2;
  }

  .procedure-card__type {
    font-size: 0.8rem;
  }

  .procedure-card__employee {
    gap: 0.6rem;
  }

  .procedure-card__avatar-fallback {
    width: 2.1rem;
    height: 2.1rem;
    font-size: 0.74rem;
  }

  .procedure-card__avatar {
    width: 2.1rem;
    height: 2.1rem;
  }

  .procedure-card__employee-name {
    font-size: 0.95rem;
  }

  .procedure-card__employee-date {
    font-size: 0.78rem;
  }

  .procedure-card__details {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem 0.8rem;
  }

  .procedure-card__detail dt {
    font-size: 0.68rem;
    margin-bottom: 0.2rem;
  }

  .procedure-card__detail dd {
    font-size: 0.94rem;
    line-height: 1.25;
  }

  .procedure-card__actions {
    gap: 0.35rem;
    padding-top: 0;
  }
}

@media (max-width: 360px) {
  .procedure-card__details {
    grid-template-columns: 1fr;
    gap: 0.7rem;
  }
}
</style>
